import {
  ConfirmedResellerIcon,
  InfluencerIcon,
  IntermediateResellerIcon,
  MerchantIcon
} from "assets/icons";
import { FilesUploader } from "components";
import { MediaDirectoryKeys, MediaType } from "constants/enums";
import { ProductBadge, useProductBadgesSelectorList } from "modules/products";
import { useCallback, useMemo } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Checkbox,
  FormField,
  Input,
  MediaFilePicker,
  MultipleMediaFilesPicker,
  RadioButton,
  Select
} from "ui";

import { useComputePackMinBuyingPrice } from "../../apis";
import { slugifyShortId } from "../../helpers";
import { PackFormValues } from "../../packs.types";

import styles from "./PackForm.module.scss";

interface PackFormHeaderProps {
  isEdit: boolean;
}

export const PackFormHeader = ({ isEdit }: PackFormHeaderProps) => {
  const { t } = useTranslation();
  const {
    control,
    setValue,
    getValues,
    watch,
    formState: { errors }
  } = useFormContext<PackFormValues>();

  const activeDiscountType = watch("activeDiscountType");
  const activeCommissionType = watch("activeCommissionType");
  const activeInfluencerCommissionType = watch(
    "activeInfluencerCommissionType"
  );
  const discountValue = watch("discount");
  const discountAmountValue = watch("discountAmount");
  const commissionPercentageValue = watch("commissionPercentage");
  const commissionValue = watch("commission");
  const slots = watch("slots");
  const variantIdsBySlotForBuyingPrice = useMemo(
    () =>
      (slots ?? []).map((slot) => ({
        variantIds: (slot.options ?? [])
          .map((opt) => opt.productVariantId)
          .filter(Boolean)
      })),
    [slots]
  );
  const { minBuyingPriceCombination } = useComputePackMinBuyingPrice({
    slots: variantIdsBySlotForBuyingPrice
  });

  const influencerCommissionPercentageValue = watch(
    "influencerCommissionPercentage"
  );
  const influencerCommissionValue = watch("influencerCommission");

  const { badges, isLoading: isBadgesLoading } = useProductBadgesSelectorList();

  const badgeOptions = useMemo(
    () => [
      { label: t("No Badge"), value: "" },
      ...(badges?.map((badge) => ({
        label: <ProductBadge badge={badge} />,
        value: badge.id
      })) ?? [])
    ],
    [badges, t]
  );

  const {
    fields: mediaFields,
    append: appendMedia,
    move: moveMedia,
    remove: removeMedia
  } = useFieldArray({
    control,
    name: "media"
  });

  const handleTitleBlur = useCallback(() => {
    if (isEdit) return;
    const currentShortId = getValues("shortId");
    const title = getValues("title");
    if (!currentShortId && title) {
      setValue("shortId", slugifyShortId(title), { shouldValidate: true });
    }
  }, [isEdit, setValue, getValues]);

  return (
    <>
      <div className={styles.packForm__block}>
        <h4 className={styles.packForm__block__title}>{t("Media")}</h4>

        <div className={styles.packForm__galleryAndPreview}>
          <div className={styles.packForm__block}>
            <p className={styles.packForm__hint}>
              {t(
                "Upload up to 10 horizontal images for the pack PDP carousel. Drag to reorder."
              )}
            </p>

            <FormField label={t("Gallery")}>
              <FilesUploader
                onSuccessUploading={(file) => appendMedia(file)}
                uploadDirectoryKey={MediaDirectoryKeys.ProductSet}
              >
                {(uploaderProps) => (
                  <MultipleMediaFilesPicker
                    allowedMediaTypes={[MediaType.IMAGE]}
                    size="lg"
                    filesLimit={10}
                    {...uploaderProps}
                    value={mediaFields}
                    onDeleteMediaFile={removeMedia}
                    onReorder={moveMedia}
                  />
                )}
              </FilesUploader>
            </FormField>
          </div>

          <div className={styles.packForm__block}>
            <p className={styles.packForm__hint}>
              {t(
                "Vertical thumbnail shown on feed cards (home, search, category lists). Not displayed in the PDP carousel."
              )}
            </p>

            <Controller
              control={control}
              name="preview"
              render={({
                field: { value, onChange },
                fieldState: { error }
              }) => (
                <FormField
                  label={t("Feed thumbnail (vertical)")}
                  errorMessage={error?.message}
                >
                  <FilesUploader
                    onSuccessUploading={onChange}
                    uploadDirectoryKey={MediaDirectoryKeys.ProductSet}
                  >
                    {(uploaderProps) => (
                      <MediaFilePicker
                        {...uploaderProps}
                        allowedMediaTypes={[MediaType.IMAGE]}
                        value={value}
                        size="lg"
                        label={"3:4"}
                        onDeleteMediaFile={() => onChange(null)}
                      />
                    )}
                  </FilesUploader>
                </FormField>
              )}
            />
          </div>
        </div>
      </div>

      <div className={styles.packForm__line} />

      <div className={styles.packForm__block}>
        <h4 className={styles.packForm__block__title}>{t("Pack details")}</h4>

        <div className={styles.packForm__row}>
          <Controller
            control={control}
            name="title"
            render={({ field, fieldState: { error } }) => (
              <Input
                value={field.value}
                onChange={field.onChange}
                onBlur={handleTitleBlur}
                label={t("Title")}
                placeholder={t("e.g. Jewelry Box")}
                errorMessage={error?.message}
                maxLength={120}
              />
            )}
          />
          <Controller
            control={control}
            name="shortId"
            render={({ field, fieldState: { error } }) => (
              <Input
                value={field.value}
                onChange={(e) => field.onChange(e.target.value.toLowerCase())}
                label={t("Short ID")}
                placeholder={t("auto-generated from title")}
                errorMessage={error?.message}
                maxLength={40}
              />
            )}
          />
        </div>

        <Controller
          control={control}
          name="description"
          render={({ field, fieldState: { error } }) => (
            <Input
              value={field.value}
              onChange={field.onChange}
              label={t("Description")}
              placeholder={t(
                "e.g. Pick a wooden box, a chain, a pendant and earrings to assemble your gift set."
              )}
              errorMessage={error?.message}
              maxLength={2000}
            />
          )}
        />

        <Controller
          control={control}
          name="badgeId"
          render={({ field: { value, onChange } }) => (
            <Select
              label={t("Pack Badge")}
              options={badgeOptions}
              value={value ?? ""}
              isOptionsLoading={isBadgesLoading}
              onChange={(next) =>
                onChange(Array.isArray(next) ? (next[0] ?? "") : (next ?? ""))
              }
              placeholder={t("Select Pack Badge")}
            />
          )}
        />

        <Controller
          control={control}
          name="isChapshopChoice"
          render={({ field: { value, onChange } }) => (
            <Checkbox
              checked={!!value}
              onChange={(e) => onChange(e.target.checked)}
              label={t("Mark as ChapShop Choice")}
            />
          )}
        />
      </div>

      <div className={styles.packForm__line} />

      <div className={styles.packForm__block}>
        <h4 className={styles.packForm__block__title}>{t("Pricing")}</h4>
        <p className={styles.packForm__hint}>
          {t(
            "Pack price is fixed regardless of which variants the customer picks per slot. Component variant prices are ignored."
          )}
        </p>

        <div className={styles.packForm__rowThree}>
          <Controller
            control={control}
            name="price"
            render={({ field, fieldState: { error } }) => (
              <Input
                value={field.value ?? ""}
                type="number"
                onChange={field.onChange}
                label={t("Standard Price (FCFA)")}
                placeholder="0"
                errorMessage={error?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="merchantPrice"
            render={({ field, fieldState: { error } }) => (
              <Input
                value={field.value ?? ""}
                type="number"
                onChange={field.onChange}
                label={
                  <>
                    <MerchantIcon />
                    {t("Merchant Price")}
                  </>
                }
                placeholder={t("Enter Merchant Price")}
                errorMessage={error?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="intermediateResellerPrice"
            render={({ field, fieldState: { error } }) => (
              <Input
                value={field.value ?? ""}
                type="number"
                onChange={field.onChange}
                label={
                  <>
                    <IntermediateResellerIcon />
                    {t("Intermediate Reseller Price")}
                  </>
                }
                placeholder={t("Enter Intermediate Reseller Price")}
                errorMessage={error?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="confirmedResellerPrice"
            render={({ field, fieldState: { error } }) => (
              <Input
                value={field.value ?? ""}
                type="number"
                onChange={field.onChange}
                label={
                  <>
                    <ConfirmedResellerIcon />
                    {t("Confirmed Reseller Price")}
                  </>
                }
                placeholder={t("Enter Confirmed Reseller Price")}
                errorMessage={error?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="influencerPrice"
            render={({ field, fieldState: { error } }) => (
              <Input
                value={field.value ?? ""}
                type="number"
                onChange={field.onChange}
                label={
                  <>
                    <InfluencerIcon />
                    {t("Influencer Price")}
                  </>
                }
                placeholder={t("Enter Influencer Price")}
                errorMessage={error?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="recommendedSellingPrice"
            render={({ field, fieldState: { error } }) => (
              <Input
                value={field.value ?? ""}
                type="number"
                onChange={field.onChange}
                label={t("Recommended Selling Price")}
                placeholder={t("Enter Recommended Selling Price")}
                errorMessage={error?.message}
              />
            )}
          />

          {/*
            Read-only floor — sum of cheapest variant buyingPrice across the
            currently selected slot options. Computed live by the backend on
            every slots change so the admin sees what the pack's tier prices
            need to clear before saving.
          */}
          <Input
            value={
              minBuyingPriceCombination > 0
                ? minBuyingPriceCombination.toString()
                : ""
            }
            type="number"
            onChange={() => undefined}
            label={t("Buying Price (FCFA)")}
            placeholder={t("Add slot options to see the floor")}
            disabled
          />

          <div className={styles.packForm__priceUnitGroup}>
            {activeCommissionType === "percentage" ? (
              <Input
                value={commissionPercentageValue ?? ""}
                type="number"
                onChange={(e) =>
                  setValue(
                    "commissionPercentage",
                    e.target.value === "" ? null : +e.target.value
                  )
                }
                label={`${t("Commission")} (%)`}
                placeholder={t("Enter Commission")}
                errorMessage={errors?.commissionPercentage?.message}
                maxLength={2}
              />
            ) : (
              <Input
                value={commissionValue ?? ""}
                type="number"
                onChange={(e) =>
                  setValue(
                    "commission",
                    e.target.value === "" ? null : +e.target.value
                  )
                }
                label={`${t("Commission")} (FCFA)`}
                placeholder={t("Enter Fixed Commission")}
                errorMessage={errors?.commission?.message}
              />
            )}
            <Controller
              control={control}
              name="activeCommissionType"
              render={({ field: { value, onChange } }) => (
                <div className={styles.packForm__priceUnitToggle}>
                  <RadioButton
                    name="commissionType"
                    value="percentage"
                    label={t("Percentage")}
                    checked={value === "percentage"}
                    onChange={(e) => {
                      if (e.currentTarget.checked) {
                        onChange("percentage");
                        setValue("commission", null);
                        setValue("commissionPercentage", null);
                      }
                    }}
                  />
                  <RadioButton
                    name="commissionType"
                    value="fixed"
                    label={t("Fixed")}
                    checked={value === "fixed"}
                    onChange={(e) => {
                      if (e.currentTarget.checked) {
                        onChange("fixed");
                        setValue("commission", null);
                        setValue("commissionPercentage", null);
                      }
                    }}
                  />
                </div>
              )}
            />
          </div>

          <div className={styles.packForm__priceUnitGroup}>
            {activeInfluencerCommissionType === "percentage" ? (
              <Input
                value={influencerCommissionPercentageValue ?? ""}
                type="number"
                onChange={(e) =>
                  setValue(
                    "influencerCommissionPercentage",
                    e.target.value === "" ? null : +e.target.value
                  )
                }
                label={`${t("Influencer Commission")} (%)`}
                placeholder={t("Enter Commission")}
                errorMessage={errors?.influencerCommissionPercentage?.message}
                maxLength={2}
              />
            ) : (
              <Input
                value={influencerCommissionValue ?? ""}
                type="number"
                onChange={(e) =>
                  setValue(
                    "influencerCommission",
                    e.target.value === "" ? null : +e.target.value
                  )
                }
                label={`${t("Influencer Commission")} (FCFA)`}
                placeholder={t("Enter Fixed Commission")}
                errorMessage={errors?.influencerCommission?.message}
              />
            )}
            <Controller
              control={control}
              name="activeInfluencerCommissionType"
              render={({ field: { value, onChange } }) => (
                <div className={styles.packForm__priceUnitToggle}>
                  <RadioButton
                    name="influencerCommissionType"
                    value="percentage"
                    label={t("Percentage")}
                    checked={value === "percentage"}
                    onChange={(e) => {
                      if (e.currentTarget.checked) {
                        onChange("percentage");
                        setValue("influencerCommission", null);
                        setValue("influencerCommissionPercentage", null);
                      }
                    }}
                  />
                  <RadioButton
                    name="influencerCommissionType"
                    value="fixed"
                    label={t("Fixed")}
                    checked={value === "fixed"}
                    onChange={(e) => {
                      if (e.currentTarget.checked) {
                        onChange("fixed");
                        setValue("influencerCommission", null);
                        setValue("influencerCommissionPercentage", null);
                      }
                    }}
                  />
                </div>
              )}
            />
          </div>

          <div className={styles.packForm__priceUnitGroup}>
            {activeDiscountType === "percentage" ? (
              <Input
                value={discountValue ?? ""}
                type="number"
                onChange={(e) =>
                  setValue(
                    "discount",
                    e.target.value === "" ? null : +e.target.value
                  )
                }
                label={`${t("Discount")} (%)`}
                placeholder={t("Enter Discount")}
                errorMessage={errors?.discount?.message}
                maxLength={2}
              />
            ) : (
              <Input
                value={discountAmountValue ?? ""}
                type="number"
                onChange={(e) =>
                  setValue(
                    "discountAmount",
                    e.target.value === "" ? null : +e.target.value
                  )
                }
                label={`${t("Discount")} (FCFA)`}
                placeholder={t("Enter Discount")}
                errorMessage={errors?.discountAmount?.message}
              />
            )}
            <Controller
              control={control}
              name="activeDiscountType"
              render={({ field: { value, onChange } }) => (
                <div className={styles.packForm__priceUnitToggle}>
                  <RadioButton
                    name="discountType"
                    value="percentage"
                    label={t("Percentage")}
                    checked={value === "percentage"}
                    onChange={(e) => {
                      if (e.currentTarget.checked) {
                        onChange("percentage");
                        setValue("discount", null);
                        setValue("discountAmount", null);
                      }
                    }}
                  />
                  <RadioButton
                    name="discountType"
                    value="amount"
                    label={t("Fixed")}
                    checked={value === "amount"}
                    onChange={(e) => {
                      if (e.currentTarget.checked) {
                        onChange("amount");
                        setValue("discount", null);
                        setValue("discountAmount", null);
                      }
                    }}
                  />
                </div>
              )}
            />
          </div>
        </div>

        <Controller
          control={control}
          name="allowSellingBelowBuyingPrice"
          render={({ field: { value, onChange } }) => (
            <Checkbox
              checked={!!value}
              onChange={(e) => onChange(e.target.checked)}
              label={t("Allow Selling below buying price")}
            />
          )}
        />
      </div>
    </>
  );
};
